import React, { useState } from 'react'
import { Calendar } from '@/components/ui/calendar'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Mail, ChevronLeft, ChevronRight } from 'lucide-react'
import { Campaign } from '@/hooks/useCampaigns'
import { addDays, format, isSameDay, startOfMonth, endOfMonth, eachDayOfInterval, getDay } from 'date-fns'

interface CampaignCalendarViewProps {
  campaigns: Campaign[]
  onCampaignClick: (campaign: Campaign) => void
}

export function CampaignCalendarView({ campaigns, onCampaignClick }: CampaignCalendarViewProps) {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())
  
  // Get campaigns for a specific date (using created_at as campaign date for now)
  const getCampaignsForDate = (date: Date) => {
    return campaigns.filter(campaign => 
      isSameDay(new Date(campaign.created_at), date)
    )
  }

  // Get status color for campaign
  const getStatusColor = (index: number) => {
    const statusIndex = index % 3
    if (statusIndex === 0) return 'bg-orange-100 text-orange-700 border-orange-200'
    if (statusIndex === 1) return 'bg-green-100 text-green-700 border-green-200'
    return 'bg-blue-100 text-blue-700 border-blue-200'
  }

  const getStatusText = (index: number) => {
    const statusIndex = index % 3
    if (statusIndex === 0) return 'Drafted'
    if (statusIndex === 1) return 'Sent'
    return 'Scheduled'
  }

  // Generate calendar days for the current month
  const monthStart = startOfMonth(selectedDate)
  const monthEnd = endOfMonth(selectedDate)
  const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd })

  // Add padding days for calendar grid
  const startPadding = getDay(monthStart)
  const paddingDays = Array.from({ length: startPadding }, (_, i) => 
    addDays(monthStart, -(startPadding - i))
  )

  const allDays = [...paddingDays, ...calendarDays]

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-lg font-semibold">
            {format(selectedDate, 'MMMM yyyy')}
          </h2>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedDate(addDays(selectedDate, -30))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => setSelectedDate(addDays(selectedDate, 30))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="text-sm text-muted-foreground">
          {campaigns.length} campaigns total
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          {/* Days of week header */}
          <div className="grid grid-cols-7 gap-2 mb-4">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <div key={day} className="text-center text-sm font-medium text-muted-foreground py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days grid */}
          <div className="grid grid-cols-7 gap-2">
            {allDays.map((day, index) => {
              const campaignsForDay = getCampaignsForDate(day)
              const isCurrentMonth = day.getMonth() === selectedDate.getMonth()
              const isToday = isSameDay(day, new Date())

              return (
                <div
                  key={day.toISOString()}
                  className={`min-h-[120px] p-2 border rounded-lg ${
                    isCurrentMonth ? 'bg-background' : 'bg-muted/30'
                  } ${isToday ? 'ring-2 ring-primary' : ''}`}
                >
                  {/* Date number */}
                  <div className={`text-sm font-medium mb-2 ${
                    isCurrentMonth ? 'text-foreground' : 'text-muted-foreground'
                  } ${isToday ? 'text-primary font-bold' : ''}`}>
                    {day.getDate()}
                  </div>

                  {/* Campaigns for this day */}
                  <div className="space-y-1">
                    {campaignsForDay.slice(0, 3).map((campaign, campaignIndex) => (
                      <div
                        key={campaign.id}
                        className="cursor-pointer"
                        onClick={() => onCampaignClick(campaign)}
                      >
                        <div className="flex items-start gap-1 p-1 rounded text-xs hover:bg-muted/50">
                          <Mail className="h-3 w-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="truncate font-medium text-xs">
                              {campaign.name}
                            </div>
                            <Badge 
                              variant="outline" 
                              className={`text-xs h-4 ${getStatusColor(campaignIndex)}`}
                            >
                              {getStatusText(campaignIndex)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Show +X more if there are more campaigns */}
                    {campaignsForDay.length > 3 && (
                      <div className="text-xs text-muted-foreground px-1">
                        +{campaignsForDay.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}